import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Plus, Trash2, Ban, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-sonner-toast';
import { getAllBannedWords, banWord, unbanWord, BannedWord } from '../lib/bannedWords';

const BannedWordsManager: React.FC = () => {
  const [bannedWords, setBannedWords] = useState<BannedWord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [newWord, setNewWord] = useState<string>('');
  const [newReason, setNewReason] = useState<string>('');
  const [wordToDelete, setWordToDelete] = useState<BannedWord | null>(null);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchBannedWords();
  }, []);

  const fetchBannedWords = async () => {
    try {
      setLoading(true);
      const words = await getAllBannedWords();
      setBannedWords(words);
    } catch (error) {
      console.error('Error fetching banned words:', error);
      toast({
        title: 'Error',
        description: 'Failed to load banned words',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddWord = async () => {
    if (!newWord.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a word to ban',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsAdding(true);
      await banWord(newWord.trim(), newReason.trim());
      
      toast({
        title: 'Success',
        description: `Successfully banned word: "${newWord.trim()}"`,
        variant: 'default',
      });

      // Reset form and close dialog
      setNewWord('');
      setNewReason('');
      setAddDialogOpen(false);
      
      // Refresh the list
      await fetchBannedWords();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to ban word',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteClick = (word: BannedWord) => {
    setWordToDelete(word);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!wordToDelete) return;

    try {
      setIsDeleting(true);
      await unbanWord(wordToDelete.id);
      
      toast({
        title: 'Success',
        description: `Successfully unbanned word: "${wordToDelete.word}"`,
        variant: 'default',
      });

      // Close dialog and refresh list
      setDeleteDialogOpen(false);
      setWordToDelete(null);
      await fetchBannedWords();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to unban word',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-red-500" />
            Banned Words Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-muted-foreground">
                Manage words that should be excluded from AI responses in all forges.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Total banned words: <span className="font-medium">{bannedWords.length}</span>
              </p>
            </div>
            <Button onClick={() => setAddDialogOpen(true)} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Ban Word
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Banned Words Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Banned Words List
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : bannedWords.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No banned words found. Click "Ban Word" to add one.
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Word</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Banned At</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bannedWords.map((word) => (
                    <TableRow key={word.id}>
                      <TableCell className="font-medium">{word.word}</TableCell>
                      <TableCell>{word.reason || '-'}</TableCell>
                      <TableCell>{formatDate(word.bannedAt)}</TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(word)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                          title={`Unban "${word.word}"`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Word Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ban Word</DialogTitle>
            <DialogDescription>
              Add a word to the banned list. This word will be excluded from all AI responses.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Word to Ban</label>
              <Input
                placeholder="Enter word..."
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Reason (Optional)</label>
              <Textarea
                placeholder="Why is this word being banned?"
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddDialogOpen(false)}
              disabled={isAdding}
            >
              Cancel
            </Button>
            <Button onClick={handleAddWord} disabled={isAdding}>
              {isAdding ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Banning...
                </>
              ) : (
                'Ban Word'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unban Word</DialogTitle>
            <DialogDescription>
              Are you sure you want to unban "{wordToDelete?.word}"? This word will be allowed in AI responses again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Unbanning...
                </>
              ) : (
                'Unban Word'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannedWordsManager;
